/**
 * DailyChecksQuickAccess.js
 *
 * FT-80 Phase 2: Quick-access button for Daily Checks from voyage view.
 * Shows progress indicator from getTodaySummary().
 * Opens DailyCheckExecutionView when clicked.
 */

import React, { useState, useEffect } from 'react';
import { ClipboardCheck, CheckCircle, AlertCircle } from 'lucide-react';
import { useDailyChecks } from '../../../../../hooks/domain/operations/useDailyChecks';
import { useMainLogbook } from '../../../../../hooks/domain/operations/useMainLogbook';
import DailyCheckExecutionView from '../DailyCheckExecutionView';

/**
 * Compact progress ring for showing completion status
 */
const ProgressRing = ({ percentage, size = 32, strokeWidth = 3 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  // Color based on completion
  const getColor = () => {
    if (percentage === 100) return '#22c55e'; // green-500
    if (percentage >= 50) return '#06b6d4'; // cyan-500
    return '#f59e0b'; // amber-500
  };

  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={getColor()}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-300"
      />
    </svg>
  );
};

/**
 * Category picker for selecting which daily check to complete
 */
const CategoryPicker = ({ categories, onSelect, onClose }) => {
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-4 bottom-20 z-50 bg-white rounded-2xl shadow-xl border border-slate-200 max-w-md mx-auto overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <h3 className="font-semibold text-slate-800">Daily Checks</h3>
          <p className="text-sm text-slate-500">Select a category to complete</p>
        </div>

        <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => onSelect(category)}
              disabled={category.completed}
              className={`
                w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all
                ${category.completed
                  ? 'border-green-200 bg-green-50 cursor-not-allowed'
                  : 'border-slate-200 hover:border-cyan-300 hover:bg-cyan-50 active:bg-cyan-100'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  category.completed ? 'bg-green-100' : 'bg-slate-100'
                }`}>
                  {category.completed ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <ClipboardCheck className="h-5 w-5 text-slate-500" />
                  )}
                </div>
                <div className="text-left">
                  <span className={`font-medium ${
                    category.completed ? 'text-green-700' : 'text-slate-800'
                  }`}>
                    {category.label}
                  </span>
                  {category.completed && category.completedAt && (
                    <p className="text-xs text-green-600">
                      Completed at {new Date(category.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right">
                <span className={`text-sm font-medium ${
                  category.completed ? 'text-green-600' : 'text-slate-500'
                }`}>
                  {category.checks.length} checks
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="p-3 border-t border-slate-100">
          <button
            onClick={onClose}
            className="w-full py-2 text-slate-600 font-medium hover:bg-slate-50 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

/**
 * Main quick access button for voyage view
 *
 * @param {Function} onLogEntry - Callback when a daily check is completed (for voyage log)
 * @param {boolean} autoOpen - If true, automatically open the category picker on mount (used when triggered externally)
 * @param {Function} onClose - Callback when the component should close (when triggered externally)
 */
const DailyChecksQuickAccess = ({ onLogEntry, autoOpen = false, onClose }) => {
  const { getTodaySummary, getCategoriesWithStatus, completeCategory } = useDailyChecks();
  const { logDailyCheck } = useMainLogbook();  // FT-87: Get logDailyCheck to log to main logbook
  const [showPicker, setShowPicker] = useState(autoOpen);  // Start open if autoOpen is true
  const [selectedCategory, setSelectedCategory] = useState(null);

  const summary = getTodaySummary();
  const categories = getCategoriesWithStatus();

  const allComplete = summary.completed === summary.total;

  // FT-80: Handle external close (when triggered from VoyageDocument)
  const handlePickerClose = () => {
    setShowPicker(false);
    onClose?.();  // Notify parent to close if externally triggered
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    setShowPicker(false);
  };

  const handleComplete = (record) => {
    // FT-80 Phase 7: Save completion status to useDailyChecks hook
    if (selectedCategory) {
      const checkResults = record?.checks || selectedCategory.checks.map(c => ({
        id: c.id,
        text: c.text,
        passed: true
      }));
      completeCategory(
        selectedCategory.id,
        checkResults,
        record?.completedBy || { name: 'Crew Member' }
      );

      // FT-87: Log to main logbook so it appears in the Logbook page
      // FT-123: Pass full completedBy object for proper audit trail (not just .name)
      const passedCount = record?.checks?.filter(c => c.passed).length || checkResults.filter(c => c.passed).length;
      const totalCount = record?.checks?.length || checkResults.length;
      const completedAt = new Date().toISOString();
      logDailyCheck({
        category: selectedCategory.id,
        categoryLabel: selectedCategory.label,
        checksCount: totalCount,
        checksPassed: passedCount,
        checks: checkResults,
        completedAt: completedAt,
        completedBy: record?.completedBy || { name: 'Crew Member', role: 'Crew' },
        notes: record?.notes || ''   // FT-309: general notes typed on completion form
      });
    }

    // Create voyage log entry for completed daily check
    if (onLogEntry && selectedCategory) {
      const passedCount = record?.checks?.filter(c => c.passed).length || 0;
      const totalCount = record?.checks?.length || 0;

      onLogEntry({
        type: 'daily_check',
        category: selectedCategory.label,
        passed: passedCount,
        total: totalCount,
        completedBy: record?.completedBy?.name || 'Crew Member'
      });
    }
    setSelectedCategory(null);
  };

  return (
    <>
      {/* Quick Access Button */}
      <button
        onClick={() => setShowPicker(true)}
        className={`
          relative flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all w-full
          ${allComplete
            ? 'border-green-300 bg-green-50 hover:bg-green-100'
            : 'border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50'
          }
        `}
      >
        {/* Progress Ring */}
        <div className="relative">
          <ProgressRing percentage={summary.percentage} size={40} strokeWidth={4} />
          <div className="absolute inset-0 flex items-center justify-center">
            {allComplete ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <ClipboardCheck className="h-4 w-4 text-slate-500" />
            )}
          </div>
        </div>

        {/* Label and Status */}
        <div className="flex-1 text-left">
          <span className={`font-semibold ${allComplete ? 'text-green-700' : 'text-slate-800'}`}>
            Daily Checks
          </span>
          <p className={`text-sm ${allComplete ? 'text-green-600' : 'text-slate-500'}`}>
            {allComplete
              ? 'All complete'
              : `${summary.completed}/${summary.total} categories done`
            }
          </p>
        </div>

        {/* Status Badge */}
        <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
          allComplete
            ? 'bg-green-200 text-green-700'
            : summary.completed > 0
              ? 'bg-cyan-100 text-cyan-700'
              : 'bg-amber-100 text-amber-700'
        }`}>
          {summary.percentage}%
        </div>
      </button>

      {/* Category Picker Modal */}
      {showPicker && (
        <CategoryPicker
          categories={categories}
          onSelect={handleSelectCategory}
          onClose={handlePickerClose}
        />
      )}

      {/* Execution View */}
      {selectedCategory && (
        <DailyCheckExecutionView
          category={selectedCategory}
          onClose={() => setSelectedCategory(null)}
          onComplete={handleComplete}
        />
      )}
    </>
  );
};

export default DailyChecksQuickAccess;
